import React, { useState, useEffect, useRef } from "react";
import { useFormattedMessage } from "hooks";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import SideModal from "components/SideModal";
import Benefits from "./Benefits";
import Branch from "./Branch";
import AccountDetails from "./AccountDetails";
import Confirmation from "./Confirmation";
import ConsentDetails from "./ConsentDetails";
import { selectIsSparkasseAccountConnected } from "store/user/account/selectors";
import { ACCOUNT_HASH_VARIABLES } from "constants/index";

const STEP = {
    BENEFITS: "benefits",
    BRANCH: "branch",
    CONSENT_DETAILS: "consentDetails",
    ACCOUNT_DETAILS: "accountDetails",
    CONFIRMATION: "confirmation",
};

const messages = {
    linkAccount: "link_account",
    linkYourSparkasseAccount: "link_your_sparkasse_account",
    linkYourSachwertAccountSparkasse: "link_your_sachwert_account_sparkasse",
    benefitsOfLinkingAccount: "benefits_of_linking_account",
    viewInvestmentsOnePlace: "view_investments_one_place",
    trackPerformanceUnifiedDashboard: "track_performance_unified_dashboard",
    easyAccessInvestmentDetails: "easy_access_investment_details",
    linkSparkasseAccount: "link_sparkasse_account",
    sparkasse: "sparkasse",
    confirmation: "confirmation_project",
    consentDetails: "consent_details",
    yourIdentification: "your_identification",
};

const LinkSparkasseAccountModal = ({ open, onCloseButtonClick, onBackButtonClick, isSparkasseAccountConnected }) => {
    const { formatMessage } = useFormattedMessage();
    const [step, setStep] = useState(isSparkasseAccountConnected ? STEP.ACCOUNT_DETAILS : STEP.BENEFITS);
    const [backSlideEffect, setBackSlideEffect] = useState("");
    const originalHashRef = useRef(null);

    useEffect(() => {
        setStep(isSparkasseAccountConnected ? STEP.ACCOUNT_DETAILS : STEP.BENEFITS);
    }, [isSparkasseAccountConnected]);

    useEffect(() => {
        if (open && !originalHashRef.current) {
            const hash = window.location.hash.substring(1);
            if (hash === ACCOUNT_HASH_VARIABLES.ACCOUNT_LINK_SPARKASSE_IDENTIFICATION) {
                originalHashRef.current = hash;
            }
        } else if (!open) {
            originalHashRef.current = null;
        }
    }, [open]);

    const resetToInitial = () => {
        setStep(isSparkasseAccountConnected ? STEP.ACCOUNT_DETAILS : STEP.BENEFITS);
        onCloseButtonClick();
    };

    const commonProps = { backSlideEffect, setBackSlideEffect, setStep, open };

    const stepsContent = {
        [STEP.BENEFITS]: (
            <Benefits
                {...commonProps}
                showTwoButtons={
                    window.location.hash.substring(1) === ACCOUNT_HASH_VARIABLES.ACCOUNT_LINK_SPARKASSE_IDENTIFICATION
                }
            />
        ),
        [STEP.BRANCH]: <Branch {...commonProps} />,
        [STEP.CONSENT_DETAILS]: <ConsentDetails backSlideEffect={backSlideEffect} />,
        [STEP.ACCOUNT_DETAILS]: <AccountDetails {...commonProps} />,
        [STEP.CONFIRMATION]: <Confirmation {...commonProps} resetToInitial={resetToInitial} />,
    };

    const getHeading = () => {
        switch (step) {
            case STEP.CONFIRMATION:
                return formatMessage(messages.confirmation);
            case STEP.CONSENT_DETAILS:
                return formatMessage(messages.consentDetails);
            case STEP.ACCOUNT_DETAILS:
                return (
                    <>
                        {formatMessage(messages.sparkasse)} <i className="fas fa-check-circle success-color ml-1" />
                    </>
                );
            case STEP.BENEFITS:
                const hash = window.location.hash.substring(1);
                return hash === ACCOUNT_HASH_VARIABLES.ACCOUNT_LINK_SPARKASSE_IDENTIFICATION
                    ? formatMessage(messages.yourIdentification)
                    : formatMessage(messages.linkAccount);
            default:
                return formatMessage(messages.linkAccount);
        }
    };

    const handleBackClick = () => {
        if (step === STEP.BRANCH) {
            setBackSlideEffect("step-slide-right");
            setStep(STEP.BENEFITS);
        } else if (step === STEP.CONSENT_DETAILS) {
            setBackSlideEffect("step-slide-right");
            setStep(STEP.BRANCH);
        } else if (step === STEP.BENEFITS && originalHashRef.current) {
            onCloseButtonClick();
            setTimeout(() => {
                window.location.hash = `#${originalHashRef.current}`;
            }, 100);
        } else {
            onBackButtonClick();
        }
    };

    return (
        <SideModal
            className="link-sparkasse-account-modal"
            wrapClassName="px-0"
            containerClassName="mw-100 pt-0"
            heading={getHeading()}
            open={open}
            onCloseButtonClick={resetToInitial}
            isBackArrowOnTop={step !== STEP.CONFIRMATION}
            onBackButtonClick={handleBackClick}
        >
            {stepsContent[step]}
        </SideModal>
    );
};

const mapStateToProps = createStructuredSelector({
    isSparkasseAccountConnected: selectIsSparkasseAccountConnected,
});

export default connect(mapStateToProps)(LinkSparkasseAccountModal);
